import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import defaultProjectSrc from "assets/images/default-project-img.svg";
import { TagLink } from "atoms";
import type { Image } from "features/images";
import type { Project, Artist } from "features/reviewable";
import type { ArtistReview, ProjectReview } from "features/reviews";
import { api } from "utils/api";

const tagList: [string, React.ComponentProps<typeof TagLink>["variant"]][] = [
  ["Woman Artist", "founderAttributes"],
  ["LGBTQIA+ Artist", "founderAttributes"],
  ["Artist with Chronic Conditions", "founderAttributes"],
  ["Black Artist", "founderAttributes"],
  ["Latin Artist", "founderAttributes"],
  ["Asian Artist", "founderAttributes"],
  ["Indigenous Artist", "founderAttributes"],
];

type HomePageHeroBlock = {
  cta: string;
  url: string;
  image: Image;
};

type HomePageFeaturedArtistBlockAPI = {
  id: string;
  largeImage: Image;
  images: Image[];
};
type HomePageFeaturedArtistBlock = HomePageFeaturedArtistBlockAPI & {
  artist: Artist | Project;
};

type HomePageDiscoverTagBlockAPI = {
  tagURL: string;
  tagName: string;
  ids: { id: string }[];
};
type HomePageDiscoverTagBlock = HomePageDiscoverTagBlockAPI & {
  items: (Artist | Project)[];
};

type HomePageHighlightBlockAPI = {
  heading: string;
  subheading: string;
  logo?: Image;
  cta: string;
  url: string;
  ids: { id: string }[];
};
type HomePageHighlightBlock = HomePageHighlightBlockAPI & {
  entities: (Artist | Project)[];
};

type HomePageNewArtistsBlock = Artist | Project;

type HomePageReviewBlock = {
  review: ArtistReview | ProjectReview;
};

type HomePageTopReviewerBlock = {
  id: string;
  name: string;
  avatarURL?: string;
  count: number;
};

type HomePageDataShape = {
  heroBlock: HomePageHeroBlock[];
  featuredArtistBlock: HomePageFeaturedArtistBlock;
  discoverTagBlock: HomePageDiscoverTagBlock;
  highlightBlocks: HomePageHighlightBlock[];
  newestArtists: HomePageNewArtistsBlock[];
  newestReviews: HomePageReviewBlock[];
  topReviewersForMonth: HomePageTopReviewerBlock[];
};

type HomePageFormShape = {
  heroBlock?: HomePageHeroBlock[];
  featuredArtistBlock?: HomePageFeaturedArtistBlockAPI;
  discoverTagBlock?: HomePageDiscoverTagBlockAPI;
  highlightBlocks?: HomePageHighlightBlockAPI[];
};

//
// HOME PAGE HOOKS (For Home Page)
//

const getHomePageData = (version = "PUBLISHED") =>
  api.get<HomePageDataShape>(`/homepage?version=${version}`);

const projectImagePlaceholder = {
  url: defaultProjectSrc,
  contentType: "text/svg",
  importDate: "",
};

/**
 * The most basic object that satisfies HomePageDataShape type used to prevent
 * massive layout shifts while the query loads and data is unavailable.
 */
const homePageDataPlaceholder: HomePageDataShape = {
  heroBlock: Array(3)
    .fill(null)
    .map((_, i) => ({
      cta: "",
      url: `#${i}`,
      image: projectImagePlaceholder,
    })),
  discoverTagBlock: {
    tagName: "",
    tagURL: "#",
    ids: [],
    items: [],
  },
  featuredArtistBlock: {
    id: "",
    largeImage: projectImagePlaceholder,
    images: [],
    artist: {
      id: "",
      type: "ARTIST",
      name: "",
      heroImage: projectImagePlaceholder,
      links: [],
      highlights: [],
      affiliations: [],
      badges: [],
      roadmapCategories: [],
      roadmapTags: [],
      artTags: [],
      communityTags: [],
      artistCollections: [],
      hugScore: 0,
      createdAt: "",
      founderTeamTags: [],
      technicalTags: [],
      projectLists: [],
      reviewCount: 0,
      counterChefsKiss: 0,
      counterMeh: 0,
      counterMindBlowing: 0,
      counterMustHug: 0,
      counterNah: 0,
      counterNeedItNow: 0,
      counterObsessed: 0,
      counterOutOfThisWorld: 0,
      counterTakeMyMoney: 0,
      counterThoughtProvoking: 0,
      counterTotalReactions: 0,
      counterYay: 0,
    },
  },
  highlightBlocks: [
    {
      heading: "",
      subheading: "",
      cta: "",
      url: "#",
      ids: [],
      entities: [],
    },
  ],
  newestArtists: [],
  newestReviews: [],
  topReviewersForMonth: [],
};

const useHomePageData = (version = "PUBLISHED") =>
  useQuery(["homepage", "page", version], () => getHomePageData(version), {
    refetchOnWindowFocus: false,
    placeholderData: homePageDataPlaceholder,
  });

//
// HOME PAGE HOOKS (Publish Drafts)
//
const updateHomePagePublishDraft = () =>
  api.post(`/homepage/editor/publish`, { body: "{}" });

const useHomePagePublishDraft = () => {
  const { mutate, ...mutation } = useMutation(updateHomePagePublishDraft);
  return {
    publish: () => mutate(),
    ...mutation,
  };
};

//
// HOME PAGE HOOKS (For Home Editors)
//

const getHomePageEditorData = (version = "DRAFT") =>
  api.get<HomePageFormShape>(`/homepage/editor?version=${version}`);

const useHomePageEditorData = (version = "PUBLISHED") =>
  useQuery(
    ["homepage", "editor", version],
    () => getHomePageEditorData(version),
    {
      refetchOnWindowFocus: false,
    }
  );

const updateHomePageEditorData = (
  updates: Partial<HomePageFormShape>,
  version = "DRAFT"
) =>
  api.put<HomePageFormShape>(`/homepage/editor?version=${version}`, {
    body: JSON.stringify(updates),
  });

const useHomePageEditorForm = (
  homePage?: HomePageFormShape,
  version = "DRAFT"
) => {
  const { handleSubmit, ...form } = useForm<HomePageFormShape>({
    defaultValues: homePage,
  });

  const { mutate, ...mutation } = useMutation((data: HomePageFormShape) =>
    updateHomePageEditorData(data, version)
  );

  // Submit only the hero block
  const onSubmitHeroBlock = handleSubmit(({ heroBlock }) =>
    mutate({ heroBlock })
  );

  // Submit only the discover block
  const onSubmitDiscoverBlock = handleSubmit(({ discoverTagBlock }) =>
    mutate({ discoverTagBlock })
  );

  // Submit only the featured block
  const onSubmitFeaturedBlock = handleSubmit(({ featuredArtistBlock }) =>
    mutate({ featuredArtistBlock })
  );

  // Submit only the discover block
  const onSubmitHighlightBlock = handleSubmit(({ highlightBlocks }) =>
    mutate({ highlightBlocks })
  );

  return {
    onSubmitHeroBlock,
    onSubmitDiscoverBlock,
    onSubmitFeaturedBlock,
    onSubmitHighlightBlock,
    ...form,
    mutation,
  };
};

export {
  useHomePageData,
  useHomePageEditorData,
  useHomePageEditorForm,
  useHomePagePublishDraft,
  tagList,
};
export type { HomePageDataShape, HomePageHeroBlock, HomePageFormShape };
